import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Alert,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import s3Service from '../../services/s3Service';

const FileUploadField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  formData = {},
  contractId = null,
  entityId = null,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is PDF by MIME type or file extension (case-insensitive)
    const isPDFByType = file.type && file.type.toLowerCase().includes('pdf');
    const isPDFByExtension = file.name && file.name.toLowerCase().endsWith('.pdf');
    
    if (!isPDFByType && !isPDFByExtension) {
      setUploadError(t('form.validation.fileType'));
      return;
    }

    // Check file size (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setUploadError(t('form.validation.fileSize'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      let uploadResult;
      
      const id = entityId || contractId || 'temp';
      
      if (field.name === 'contract_pdf') {
        uploadResult = await s3Service.uploadContractPDF(file, id);
      } else if (field.name === 'protocol_pdf') {
        uploadResult = await s3Service.uploadProtocolPDF(file, id);
      } else if (field.name === 'proposal_file') {
        uploadResult = await s3Service.uploadOfferPDF(file, id);
      } else if (field.name === 'revised_proposal_file' || field.name.startsWith('file_')) {
        uploadResult = await s3Service.uploadRevisedOfferPDF(file, id);
      } else if (field.name === 'commissionDocument') {
        uploadResult = await s3Service.uploadJobCommissionDocument(file, id);
      } else if (field.name === 'firstPhaseDocument') {
        uploadResult = await s3Service.uploadJobFirstPhaseDocument(file, id);
      } else {
        uploadResult = await s3Service.uploadFile(file, 'temp');
      }

      if (uploadResult.success) {
        const newValue = {
          fileName: uploadResult.fileName,
          key: uploadResult.key,
          url: uploadResult.url,
          size: uploadResult.size || file.size,
          type: uploadResult.type || file.type,
        };

        if (onChange) {
          onChange(field.name, newValue);
        }
        
        setUploadProgress(100);
      } else {
        // Backend'den gelen hata mesajını göster
        let errorMessage = uploadResult.error || t('form.error.uploadFailed');
        
        // Backend'den "too large" hatası gelirse, daha açıklayıcı bir mesaj göster
        if (errorMessage.toLowerCase().includes('too large') || 
            errorMessage.toLowerCase().includes('file size') ||
            errorMessage.toLowerCase().includes('exceed')) {
          const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
          errorMessage = `Dosya boyutu çok büyük (${fileSizeMB} MB). Backend limiti aşıldı. Lütfen daha küçük bir dosya seçin.`;
        }
        
        setUploadError(errorMessage);
      }
    } catch (error) {
      console.error('File upload error:', error);
      setUploadError(t('form.error.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async () => {
    if (!value?.key) return;

    try {
      const deleteResult = await s3Service.deleteFile(value.key);
      if (deleteResult.success) {
        if (onChange) {
          onChange(field.name, null);
        }
      } else {
        setUploadError(t('form.error.deleteFailed'));
      }
    } catch (error) {
      console.error('File delete error:', error);
      setUploadError(t('form.error.deleteFailed'));
    }
  };

  const handleDownload = async () => {
    if (value?.key) {
      try {
        const signedUrl = await s3Service.getSignedUrl(value.key);
        window.open(signedUrl, '_blank');
      } catch (error) {
        console.error('Download error:', error);
        setUploadError('Download failed');
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {field.label && t(field.label)}
        {field.required && <span style={{ color: 'red' }}> *</span>}
      </Typography>

      {/* Dosya Yükleme Alanı */}
      {!value && (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: error ? 'error.main' : 'grey.300',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            backgroundColor: 'grey.50',
            cursor: uploading ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.6 : 1,
          }}
          onClick={() => !uploading && !disabled && fileInputRef.current?.click()}
        >
          <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {t('form.upload.clickToUpload')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('form.upload.pdfOnly')} (Max 10MB)
          </Typography>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading || disabled}
          />
        </Box>
      )}

      {/* Yükleme Progress */}
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="textSecondary">
            {t('form.upload.uploading')}... {uploadProgress}%
          </Typography>
        </Box>
      )}

            {/* Yüklenen Dosya */}
      {value && !uploading && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <FileIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="body2" noWrap>
                {value.fileName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {value.size && !isNaN(value.size) ? (value.size / 1024 / 1024).toFixed(2) : '0'} MB
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleDownload}
              disabled={disabled}
            >
              <FileIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDeleteFile}
              disabled={disabled}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Hata Mesajı */}
      {uploadError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {uploadError}
        </Alert>
      )}

      {/* Form Validation Error */}
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FileUploadField; 